import type { ReactNode } from 'react';
import { CaretRight, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';
import { Card } from '../ui/card';

/** A titled white card of settings rows (Figma A16 "Admin/Settings card"). */
export function SettingCard({
  title,
  subtitle,
  action,
  children,
  className,
}: {
  title: string;
  subtitle?: ReactNode;
  /** Right-aligned link-style action in the header, e.g. "Sign out of all other devices". */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card padding="lg" className={cx('flex flex-col gap-(--mbm-space-4)', className)}>
      <header className="flex items-start justify-between gap-(--mbm-space-4)">
        <div className="flex min-w-0 flex-col gap-(--mbm-space-1)">
          <h2 className="text-heading-h4 text-text-primary">{title}</h2>
          {subtitle ? <p className="text-body-small text-text-secondary">{subtitle}</p> : null}
        </div>
        {action}
      </header>
      <div className="flex flex-col">{children}</div>
    </Card>
  );
}

export interface SettingRowProps {
  icon?: PhosphorIcon;
  /** A custom glyph (e.g. the Google logo) when no Phosphor icon fits. */
  iconNode?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right side: a Button, Toggle, StatusTag or `<RowValue>`. */
  trailing?: ReactNode;
  /** Makes the whole row a button (value rows that open an editor). */
  onClick?: () => void;
  muted?: boolean;
  className?: string;
}

const ROW = 'flex w-full items-center gap-(--mbm-space-4) py-(--mbm-space-4) text-left';

/**
 * One row of a settings card: a plain icon, a title with a one-line explanation, and one trailing control
 * (a Button, a Toggle, a status tag, or a value with a chevron). Mirrors the Admin/Settings row components.
 */
export function SettingRow({
  icon,
  iconNode,
  title,
  subtitle,
  trailing,
  onClick,
  muted = false,
  className,
}: SettingRowProps) {
  const glyph = icon ? <Icon icon={icon} size="md" /> : iconNode;
  const content = (
    <>
      {glyph ? (
        <span className="flex w-(--mbm-size-icon-tile) shrink-0 justify-center text-text-secondary">{glyph}</span>
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={cx('text-label-large', muted ? 'text-text-disabled' : 'text-text-primary')}>{title}</span>
        {subtitle ? (
          <span className={cx('text-body-small', muted ? 'text-text-disabled' : 'text-text-secondary')}>
            {subtitle}
          </span>
        ) : null}
      </span>
      {trailing}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cx(
          ROW,
          'rounded-(--mbm-radius-sm) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
          className,
        )}
      >
        {content}
      </button>
    );
  }
  return <div className={cx(ROW, className)}>{content}</div>;
}

/** The grey value + chevron on the right of a row that opens an editor. */
export function RowValue({ children }: { children: ReactNode }) {
  return (
    <span className="flex shrink-0 items-center gap-(--mbm-space-2) text-body-compact text-text-secondary">
      {children}
      <Icon icon={CaretRight} size="sm" />
    </span>
  );
}

/** Two columns on desktop, one on a phone: the form grid inside a settings card. */
export function FieldGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('grid gap-(--mbm-space-4) sm:grid-cols-2', className)}>{children}</div>;
}

/** "Last changed by Priya Sharma · 3 Oct 2026" under a page of workspace settings. */
export function LastChanged({ by, date, prefix = 'Last changed' }: { by?: string; date?: string; prefix?: string }) {
  if (!by || !date) return null;
  return (
    <p className="px-(--mbm-space-1) text-body-small text-text-tertiary">
      {prefix} by {by} · {date}
    </p>
  );
}
